import "video.js/dist/video-js.css"

import { useEffect, useRef, useState } from "react"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import videojs from "video.js"
import Player from "video.js/dist/types/player"

type Props = {
  poster: string
  src: string
  id: number
  slug: string
  title: string
  thumbnailComponent?: React.ReactNode
  triggerConfirmWatch: () => void
}

// const DURATION_ALLOWED_TO_FREE_USER = 10 * 60 // in seconds

const STORAGE_KEY = "masterclassVideoProgress"
const SAVE_INTERVAL = 3 // seconds
const VIDEO_PROGRESS_UPDATE_INTERVAL = 10 // seconds

const defaultPlayerOptions = {
  userActions: {
    // video.js's default handler leaves the fullscreen promise unhandled. Double taps
    // synthesized by drag-drop-touch aren't user gestures, so browsers reject them.
    doubleClick() {
      const player = this as unknown as Player
      if (player.isFullscreen())
        Promise.resolve(player.exitFullscreen()).catch(() => {})
      else Promise.resolve(player.requestFullscreen()).catch(() => {})
    },
    hotkeys(event: KeyboardEvent) {
      const player = this as unknown as Player

      switch (event.key) {
        case "m":
          player.muted(!player.muted())
          break
        case "f":
          if (player.isFullscreen()) player.exitFullscreen()
          else player.requestFullscreen()
          break
        case "p":
        case " ":
          event.preventDefault()
          if (player.paused()) player.play()
          else player.pause()
          break
        case "ArrowUp":
          player.volume(Math.min(1, (player.volume() || 0) + 0.1))
          break
        case "ArrowDown":
          player.volume(Math.max(0, (player.volume() || 0) - 0.1))
          break
        case "ArrowRight":
          player.currentTime((player.currentTime() || 0) + 5)
          break
        case "ArrowLeft":
          player.currentTime((player.currentTime() || 0) - 5)
          break
        default:
          break
      }
    },
  },
  autoplay: false,
  width: "100%",
  aspectRatio: "16:9",
  fluid: true,
  controls: true,
  responsive: true,
}

export const MasterclassRecordingVideoPlayer = ({
  poster,
  src,
  id,
  slug,
  title,
  thumbnailComponent,
  triggerConfirmWatch,
}: Props) => {
  const videoContainer = useRef<HTMLDivElement>(null)
  const playerRef = useRef<Player | null>(null)
  const lastSaved = useRef(0)

  const hasTriggeredConfirmRef = useRef(false)

  const hasSuraasaPlus = true
  const totalWatchTimeRef = useRef(0)
  const lastAnalyticsPosition = useRef(0)
  const lastProgressSentAt = useRef(0)
  const isSeekingRef = useRef(false)

  const [isPlaying, setIsPlaying] = useState(false)

  const loadSavedTimestamp = (player: Player) => {
    try {
      const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {}
      const lastSeen = map[id]

      if (typeof lastSeen === "number" && lastSeen > 0) {
        player.one("loadedmetadata", () => {
          player.currentTime(lastSeen)
        })
      }
    } catch {
      // ignore
    }
  }

  const saveTimestamp = (time: number) => {
    try {
      const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {}
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...map,
          [id]: Math.floor(time),
        })
      )
    } catch {
      // ignore
    }
  }

  const clearTimestamp = () => {
    try {
      const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {}
      delete map[id]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (hasSuraasaPlus === undefined) return

    const options = {
      ...defaultPlayerOptions,
      ...{
        poster,
        sources: [{ src }],
      },
    }

    // const initOverlay = (player: Player) => {
    //   const controls = document.querySelector(".vjs-control-bar")

    //   if (controls) {
    //     const container = document.createElement("div")
    //     container.classList.add("vjs-overlay-content")
    //     ReactDOM.createRoot(container).render(
    //       <OverlayContent player={player} />
    //     )
    //     controls.parentNode?.insertBefore(container, controls.previousSibling)
    //   }
    // }

    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js")

      if (!videoContainer.current) return

      videoElement.classList.add("vjs-big-play-centered")
      videoContainer.current.appendChild(videoElement)

      const player = videojs(videoElement, options, () => {
        videojs.log("player is ready")
        loadSavedTimestamp(player)
      })
      playerRef.current = player
      lastProgressSentAt.current = Date.now()

      setIsPlaying(!player.paused())

      player.on("seeking", () => {
        isSeekingRef.current = true
      })

      player.on("seeked", () => {
        lastAnalyticsPosition.current = Math.floor(player.currentTime() || 0)
        lastProgressSentAt.current = Date.now()
        isSeekingRef.current = false
      })

      player.on("timeupdate", () => {
        const time = Math.floor(player.currentTime() || 0)

        if (Math.abs(time - lastSaved.current) >= SAVE_INTERVAL) {
          lastSaved.current = time
          saveTimestamp(time)
        }
      })

      player.on("timeupdate", () => {
        if (player.paused()) return
        if (isSeekingRef.current) return

        const now = Date.now()
        const elapsedSec = (now - lastProgressSentAt.current) / 1000

        if (elapsedSec < VIDEO_PROGRESS_UPDATE_INTERVAL) return

        const currentTime = Math.floor(player.currentTime() || 0)
        const delta = currentTime - lastAnalyticsPosition.current

        if (delta <= 0 || delta > VIDEO_PROGRESS_UPDATE_INTERVAL + 1) {
          lastAnalyticsPosition.current = currentTime
          lastProgressSentAt.current = now
          return
        }

        totalWatchTimeRef.current += delta

        trackingService.trackEvent(
          TrackingEvents.masterclassVideoProgress,
          {
            masterclass_id: id ?? slug,
            masterclass_title: title,
            video_src: src,
            watch_time_interval: delta,
            current_playback_position: currentTime,
            total_video_duration: Math.floor(player.duration() || 0),
          },
          { services: ["PostHog"] }
        )

        lastAnalyticsPosition.current = currentTime
        lastProgressSentAt.current = now
      })

      player.on("ended", () => {
        trackingService.trackEvent(
          TrackingEvents.masterclassVideoCompleted,
          {
            masterclass_id: id ?? slug,
            masterclass_title: title,
            video_src: src,
            total_watch_time: totalWatchTimeRef.current,
            total_video_duration: Math.floor(player.duration() || 0),
          },
          { services: ["PostHog"] }
        )

        totalWatchTimeRef.current = 0
        lastSaved.current = 0
        clearTimestamp()
        setIsPlaying(false)
      })

      player.on("play", () => {
        setIsPlaying(true)

        if (!hasTriggeredConfirmRef.current) {
          triggerConfirmWatch()
          lastAnalyticsPosition.current = Math.floor(player.currentTime() || 0)
          lastProgressSentAt.current = Date.now()
          trackingService.trackEvent(
            TrackingEvents.masterclassVideoStarted,
            {
              masterclass_id: id ?? slug,
              masterclass_title: title,
              video_src: src,
              total_video_duration: Math.floor(player.duration() || 0),
              current_playback_position: Math.floor(player.currentTime() || 0),
            },
            {
              services: ["PostHog"],
            }
          )
        }

        hasTriggeredConfirmRef.current = true
      })

      player.on("pause", () => setIsPlaying(false))

      // if (!hasSuraasaPlus) initOverlay(player)
      // videoElement.appendChild(overlayContent)

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      const player = playerRef.current

      player.autoplay(options.autoplay)
      player.src(options.sources)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, poster, hasSuraasaPlus, id])

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current

    return () => {
      if (player && !player.isDisposed()) {
        const t = player.currentTime() ?? 0
        const d = player.duration() ?? 0

        if (d - t > 5) {
          saveTimestamp(t)
        }

        player.dispose()
        playerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef])

  return (
    <div className="relative" data-vjs-player>
      <div ref={videoContainer} />

      {thumbnailComponent && !isPlaying && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Play video"
          className="pointer-events-auto absolute inset-0 flex items-center justify-center"
          onClick={() => {
            if (!playerRef.current) {
              const el = videoContainer.current?.querySelector(
                ".vjs-big-play-button"
              ) as HTMLButtonElement | null
              if (el) el.click()
              return
            }
            playerRef.current.play()
          }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              playerRef.current?.play()
            }
          }}
        >
          <div className="flex size-full items-center justify-center overflow-hidden">
            {thumbnailComponent}
          </div>
        </div>
      )}
    </div>
  )
}

// const OverlayContent = ({ player }: { player: Player }) => {
//   const [currentTime, setCurrentTime] = useState(0)

//   useEffect(() => {
//     if (!player) return

//     function onTimeUpdate() {
//       if (!player) return

//       const time = Math.round(player.currentTime() || 0)
//       setCurrentTime(time)
//     }

//     if (player) {
//       player.on("timeupdate", onTimeUpdate)
//     }

//     return () => {
//       if (player) {
//         player.off("timeupdate", onTimeUpdate)
//       }
//     }
//   }, [player])

//   const getTimeLeft = () => {
//     const timeLeft = DURATION_ALLOWED_TO_FREE_USER - currentTime

//     if (timeLeft <= 0) {
//       return ""
//     }

//     const hours = Math.floor(timeLeft / 3600)
//     const minutes = Math.floor((timeLeft % 3600) / 60)
//     const seconds = Math.floor(timeLeft % 60)

//     const pad = (num: number) => num.toString().padStart(2, "0")

//     if (hours > 0) {
//       return `${pad(hours)}:${pad(minutes)}:${pad(seconds)} left`
//     }

//     return `${pad(minutes)}:${pad(seconds)} left`
//   }

//   const timeLeft = getTimeLeft()

//   return (
//     <div className="p-3">
//       <div className="flex items-center justify-between">
//         <div>
//           <img
//             alt="Suraasa Plus"
//             height={100}
//             src="https://assets.suraasa.com/logos/suraasa-plus/svg/long-white.svg"
//             width={300}
//           />
//           <p className="text-lg">
//             Subscribe to Suraasa Plus to watch the full video
//           </p>
//         </div>

//         {timeLeft && (
//           <div>
//             <span className="rounded-[4px] bg-[rgba(15,23,42,0.6)] p-1 text-base font-bold">
//               {timeLeft}
//             </span>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

export default MasterclassRecordingVideoPlayer
